import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { CalcScreen } from '../../components/shell';
import { C, FONT, R, SP } from '../../theme/tokens';

type IsoClass = {
  isoClass: string;
  particles: Record<string, string>;
  description: string;
};

const ISO_CLASSES: IsoClass[] = [
  { isoClass: 'ISO Class 1', particles: { '0.1 μm': '10', '0.2 μm': '2', '0.3 μm': '-', '0.5 μm': '-', '1.0 μm': '-', '5.0 μm': '-' }, description: 'Ultra-clean environments for nanotechnology and advanced semiconductor fabrication.' },
  { isoClass: 'ISO Class 2', particles: { '0.1 μm': '100', '0.2 μm': '24', '0.3 μm': '10', '0.5 μm': '4', '1.0 μm': '-', '5.0 μm': '-' }, description: 'Semiconductor manufacturing and critical medical devices.' },
  { isoClass: 'ISO Class 3', particles: { '0.1 μm': '1,000', '0.2 μm': '237', '0.3 μm': '102', '0.5 μm': '35', '1.0 μm': '8', '5.0 μm': '-' }, description: 'Semiconductor manufacturing (equivalent to FS 209E Class 1).' },
  { isoClass: 'ISO Class 4', particles: { '0.1 μm': '10,000', '0.2 μm': '2,370', '0.3 μm': '1,020', '0.5 μm': '352', '1.0 μm': '83', '5.0 μm': '-' }, description: 'Pharmaceutical manufacturing (equivalent to FS 209E Class 10).' },
  { isoClass: 'ISO Class 5', particles: { '0.1 μm': '100,000', '0.2 μm': '23,700', '0.3 μm': '10,200', '0.5 μm': '3,520', '1.0 μm': '832', '5.0 μm': '29' }, description: 'Sterile compounding and aseptic filling (FS 209E Class 100).' },
  { isoClass: 'ISO Class 6', particles: { '0.1 μm': '1,000,000', '0.2 μm': '237,000', '0.3 μm': '102,000', '0.5 μm': '35,200', '1.0 μm': '8,320', '5.0 μm': '293' }, description: 'Medical device manufacturing (FS 209E Class 1,000).' },
  { isoClass: 'ISO Class 7', particles: { '0.1 μm': '-', '0.2 μm': '-', '0.3 μm': '-', '0.5 μm': '352,000', '1.0 μm': '83,200', '5.0 μm': '2,930' }, description: 'General cleanroom applications (FS 209E Class 10,000).' },
  { isoClass: 'ISO Class 8', particles: { '0.1 μm': '-', '0.2 μm': '-', '0.3 μm': '-', '0.5 μm': '3,520,000', '1.0 μm': '832,000', '5.0 μm': '29,300' }, description: 'Light manufacturing and assembly areas (FS 209E Class 100,000).' },
  { isoClass: 'ISO Class 9', particles: { '0.1 μm': '-', '0.2 μm': '-', '0.3 μm': '-', '0.5 μm': '35,200,000', '1.0 μm': '8,320,000', '5.0 μm': '293,000' }, description: 'Clean zones (approximately typical office environment).' },
];

const FS209E: { fs209e: string; iso14644: string }[] = [
  { fs209e: 'Class 1', iso14644: 'ISO Class 3' },
  { fs209e: 'Class 10', iso14644: 'ISO Class 4' },
  { fs209e: 'Class 100', iso14644: 'ISO Class 5' },
  { fs209e: 'Class 1,000', iso14644: 'ISO Class 6' },
  { fs209e: 'Class 10,000', iso14644: 'ISO Class 7' },
  { fs209e: 'Class 100,000', iso14644: 'ISO Class 8' },
];

export default function CleanroomClassificationsScreen() {
  return (
    <CalcScreen title="Cleanroom Classifications" subtitle="ISO 14644-1:2015 classification">
      <Text style={styles.sectionLabel}>ISO 14644-1:2015 Classes</Text>
      <Text style={styles.note}>Maximum allowable particle concentrations (particles/m³).</Text>

      {ISO_CLASSES.map((cls) => (
        <View key={cls.isoClass} style={styles.card}>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{cls.isoClass}</Text>
          </View>

          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={styles.tableHeaderText}>Particle Size</Text>
              <Text style={styles.tableHeaderText}>Max (per m³)</Text>
            </View>
            {Object.entries(cls.particles).map(([size, limit]) => (
              <View key={size} style={styles.tableRow}>
                <Text style={styles.sizeText}>{size}</Text>
                <Text style={[styles.limitText, limit === '-' && styles.dashText]}>{limit}</Text>
              </View>
            ))}
          </View>

          <Text style={styles.description}>{cls.description}</Text>
        </View>
      ))}

      <Text style={styles.sectionLabel}>FS 209E Cross-Reference</Text>
      <Text style={styles.note}>Obsolete Federal Standard 209E equivalents to ISO 14644-1.</Text>

      <View style={styles.card}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderText}>FS 209E (Obsolete)</Text>
          <Text style={styles.tableHeaderText}>ISO 14644-1 (Current)</Text>
        </View>
        {FS209E.map((e) => (
          <View key={e.fs209e} style={styles.tableRow}>
            <Text style={styles.sizeText}>{e.fs209e}</Text>
            <Text style={styles.limitText}>{e.iso14644}</Text>
          </View>
        ))}
        <Text style={styles.footnote}>
          FS 209E was cancelled in 2001 and superseded by ISO 14644-1. FS 209E used English units
          (particles/ft³); ISO 14644-1 uses metric (particles/m³).
        </Text>
      </View>

      <Text style={styles.sectionLabel}>Standards Reference</Text>
      <View style={styles.card}>
        <Text style={styles.refTitle}>ISO 14644-1:2015</Text>
        <Text style={styles.description}>
          Cleanrooms and associated controlled environments — Part 1: Classification of air
          cleanliness by particle concentration.
        </Text>
        <Text style={styles.bullet}>• Supersedes ISO 14644-1:1999.</Text>
        <Text style={styles.bullet}>• Cancels and replaces Federal Standard 209E (1992).</Text>
        <Text style={styles.bullet}>• Particle counts based on cubic meter (SI units).</Text>
        <Text style={styles.bullet}>• Classes range from ISO Class 1 (cleanest) to ISO Class 9.</Text>

        <Text style={[styles.refTitle, { marginTop: SP.md }]}>Testing References</Text>
        <Text style={styles.bullet}>• ISO 14644-1 — air cleanliness by particle concentration.</Text>
        <Text style={styles.bullet}>• IEST recommended practices for testing and certification.</Text>
        <Text style={styles.bullet}>• Project-specific test plans and acceptance criteria.</Text>
      </View>
    </CalcScreen>
  );
}

const styles = StyleSheet.create({
  sectionLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: SP.md,
    marginBottom: SP.sm,
  },
  note: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    marginBottom: SP.sm,
  },
  card: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.md,
    marginBottom: SP.sm + 4,
  },
  badge: {
    alignSelf: 'flex-start',
    backgroundColor: C.panel2,
    borderWidth: 1,
    borderColor: C.edge,
    paddingHorizontal: SP.sm + 4,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: SP.sm,
  },
  badgeText: {
    fontFamily: FONT.sansBold,
    fontSize: 13,
    color: C.ink,
    letterSpacing: 0.3,
  },
  table: {
    marginBottom: SP.sm,
  },
  tableHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: C.edge,
    marginBottom: 6,
  },
  tableHeaderText: {
    fontFamily: FONT.sansMed,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  tableRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  sizeText: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
  },
  limitText: {
    fontFamily: FONT.mono,
    fontSize: 13,
    color: C.ink,
  },
  dashText: {
    color: C.dim,
  },
  description: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    lineHeight: 19,
    marginTop: SP.xs,
  },
  footnote: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    lineHeight: 17,
    marginTop: SP.sm,
  },
  refTitle: {
    fontFamily: FONT.sansBold,
    fontSize: 15,
    color: C.ink,
    marginBottom: SP.xs,
  },
  bullet: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    lineHeight: 19,
    marginLeft: SP.sm,
  },
});
